import { RefreshCw, XCircle, Clock, Mail } from "lucide-react";

export default function RefundPolicy() {
    return (
        <div className="max-w-3xl mx-auto px-6 py-16" data-testid="refund-policy-page">
            <div className="mb-10">
                <div className="overline mb-3">Legal</div>
                <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-4">
                    Cancellation &amp; Refund Policy<span className="text-accent">.</span>
                </h1>
                <p className="text-zinc-500">Last updated: February 2026</p>
            </div>

            <div className="surface p-8 mb-6 flex items-start gap-4">
                <div className="w-10 h-10 rounded-md border border-accent/30 bg-accent/10 flex-shrink-0 flex items-center justify-center">
                    <RefreshCw className="w-4 h-4 text-accent" strokeWidth={1.75} />
                </div>
                <div>
                    <h2 className="font-heading text-lg font-semibold text-zinc-100 mb-1">
                        Fair-use refund window
                    </h2>
                    <p className="text-sm text-zinc-400 leading-relaxed">
                        listcraft sells one-time credit packs, not recurring subscriptions. There is nothing
                        to cancel — but if something goes wrong, we&apos;ll make it right.
                    </p>
                </div>
            </div>

            <div className="space-y-8 text-zinc-300 leading-relaxed">
                <section>
                    <h3 className="font-heading text-xl font-semibold text-zinc-100 mb-3 flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-accent" /> Cancellation
                    </h3>
                    <ul className="space-y-2 text-sm text-zinc-400 list-disc pl-5">
                        <li>listcraft does not sell recurring subscriptions — there is nothing to cancel.</li>
                        <li>You may stop using the service at any time; unused credits remain on your account and never expire.</li>
                        <li>If you wish to delete your account entirely, email us at <a href="mailto:arienterprises50@gmail.com" className="text-accent hover:underline">arienterprises50@gmail.com</a> and we will process it within 7 business days.</li>
                    </ul>
                </section>

                <section>
                    <h3 className="font-heading text-xl font-semibold text-zinc-100 mb-3 flex items-center gap-2">
                        <RefreshCw className="w-4 h-4 text-accent" /> When you can request a refund
                    </h3>
                    <ul className="space-y-2 text-sm text-zinc-400 list-disc pl-5">
                        <li><span className="text-zinc-200">Duplicate charges</span> — full refund of the extra charge.</li>
                        <li><span className="text-zinc-200">Payment captured but credits not delivered</span> — full refund if we cannot reconcile within 3 business days.</li>
                        <li><span className="text-zinc-200">Unused credits within 7 days of purchase</span> — full refund of the pack, provided none of the credits from that purchase have been consumed.</li>
                    </ul>
                </section>

                <section>
                    <h3 className="font-heading text-xl font-semibold text-zinc-100 mb-3">Non-refundable</h3>
                    <ul className="space-y-2 text-sm text-zinc-400 list-disc pl-5">
                        <li>Credits that have already been used to generate listings.</li>
                        <li>Requests made more than 7 days after purchase, unless there is a delivery failure on our side.</li>
                        <li>Dissatisfaction with generated copy where the credit has been consumed — please review the output preview and use the regenerate option before consuming your final credit.</li>
                    </ul>
                </section>

                <section>
                    <h3 className="font-heading text-xl font-semibold text-zinc-100 mb-3 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-accent" /> Refund timeline
                    </h3>
                    <p className="text-sm text-zinc-400">
                        Approved refunds are initiated within 3 business days and typically reflect in your
                        original payment method within 5–7 business days depending on your bank or UPI
                        provider.
                    </p>
                </section>

                <section>
                    <h3 className="font-heading text-xl font-semibold text-zinc-100 mb-3 flex items-center gap-2">
                        <Mail className="w-4 h-4 text-accent" /> How to raise a request
                    </h3>
                    <p className="text-sm text-zinc-400">
                        Email <a href="mailto:arienterprises50@gmail.com" className="text-accent hover:underline">arienterprises50@gmail.com</a> from
                        the address registered to your listcraft account. Include your Razorpay payment
                        reference ID and a short description of the issue. We aim to respond within 24
                        business hours.
                    </p>
                    <p className="text-sm text-zinc-500 mt-4">
                        Ari Enterprises · listcraft
                    </p>
                </section>
            </div>
        </div>
    );
}
